import { BaseOperation } from 'src/app/perical/models/thermal-operation.model';
import { PressureResults, TemperatureResults } from 'src/app/perical/models/thermal-results.model';
import { GetValueFromFahrenheit, GetValueFromPsi } from 'src/app/perivis/shared/helpers/units.helper';

export interface EnvelopeCurve {
  x: number[];
  y: number[];
}

export function convertDefaultEnvelopeByUnits(
  defaultEnvelope: EnvelopeCurve,
  temperatureUnit: string,
  pressureUnit: string
): EnvelopeCurve {
  const converted: EnvelopeCurve = {
    x: [...defaultEnvelope.x],
    y: [...defaultEnvelope.y]
  };

  converted.x = converted.x.map(x => GetValueFromFahrenheit(x, temperatureUnit));
  converted.y = converted.y.map(y => GetValueFromPsi(y, pressureUnit));

  return converted;
}

export function mapTemperatureResultsToOperations(temperatureResults: TemperatureResults[]): Partial<BaseOperation>[] {
  return temperatureResults.map(x => ({
    name: x.name,
    operationId: x.operationId,
  }));
}

export function uniqueById<T extends { id: string }>(items: T[]): T[] {
  return items.filter((item, index, self) => index === self.findIndex(x => x.id === item.id));
}

export function createPoints(x: number[], y: number[]): [number, number][] {
  if (x.length !== y.length) {
    throw new Error("The 'x' and 'y' arrays must have the same length.");
  }

  return x.map((value, index) => [value, y[index]]);
}

function toRowObject(keys: string[], values: number[]): Record<string, number> {
  return keys.reduce((acc: Record<string, number>, key, index) => {
    acc[key] = values[index];
    return acc;
  }, {});
}

export function buildWellheadAndPerfsTraces(
  resultIndex: number,
  perforationDepth: number,
  ops: any[] | false,
  operation: any,
  legendGroupName: string,
  temperatureResults: TemperatureResults[],
  pressureResults: PressureResults[]
): any[] {
  const tempRows = temperatureResults[resultIndex].phases[0].temperatureResultsTable.rows;
  const pressRows = pressureResults[resultIndex].phases[0].pressureResultsTable.rows;

  const rowXEntry = tempRows.find(x => (perforationDepth - x[0]) < 0.001);
  const rowYEntry = pressRows.find(x => (perforationDepth - x[0]) < 0.001);

  if (!rowXEntry || !rowYEntry) {
    return [];
  }

  const isFirstOperation = ops ? (ops.length >= 1 ? ops.indexOf(operation) === 0 : false) : true;

  const wellheadPoint = {
    x: [tempRows[0][2]],
    y: [pressRows[0][2]],
    name: 'Wellhead',
    mode: 'markers',
    marker: { size: 10, color: '#999999' },
    showlegend: isFirstOperation,
    legendgroup: legendGroupName
  };

  const shoePoint = {
    x: [rowXEntry[2]],
    y: [rowYEntry[2]],
    name: 'Perforations',
    mode: 'markers',
    marker: { size: 10, color: '#FF0000' },
    showlegend: isFirstOperation,
    legendgroup: legendGroupName
  };

  return [wellheadPoint, shoePoint];
}

export function buildSingleModeTraces(
  envelopeData: any,
  resultIndex: number,
  operation: any,
  perforationDepth: number | undefined,
  temperatureResults: TemperatureResults[],
  pressureResults: PressureResults[],
  temperatureUnit: string,
  pressureUnit: string,
  depthUnit: string,
  colFields: string[]
): { traces: any[]; gridPhaseEnvelope: any[]; gridOperationData: any[] } {
  const traces: any[] = [];

  if (envelopeData) {
    envelopeData['name'] = 'Phase Envelope';
    traces.push(envelopeData);
  }

  const perforationIndex = perforationDepth != null
    ? temperatureResults[resultIndex].phases[0].temperatureResultsTable.rows.findIndex(x => (perforationDepth - x[0]) < 0.001)
    : -1;

  const temperatureRows = temperatureResults[resultIndex].phases[0].temperatureResultsTable.rows.slice(0, perforationIndex + 1);
  const pressureRows = pressureResults[resultIndex].phases[0].pressureResultsTable.rows.slice(0, perforationIndex + 1);

  const temperatures = temperatureRows.map(x => x[2]);
  const pressures = pressureRows.map(x => x[2]);
  const depths = temperatureRows.map(x => x[0]);

  const legendGroupName = 'group' + resultIndex;

  const markerTraces = perforationDepth != null
    ? buildWellheadAndPerfsTraces(resultIndex, perforationDepth, false, operation, legendGroupName, temperatureResults, pressureResults)
    : [];

  const tempPressureLine = {
    x: temperatures,
    y: pressures,
    name: temperatureResults[resultIndex].name,
    legendgroup: legendGroupName,
    customdata: depths,
    hovertemplate: `Temperature: %{x} ${temperatureUnit}<br>Pressure: %{y} ${pressureUnit}<br>Depth: %{customdata} ${depthUnit}<extra></extra>`
  };

  traces.push(...markerTraces, tempPressureLine);

  const pressTempRes = createPoints(pressures, temperatures);
  const gridOperationData = pressTempRes.map(r => toRowObject(colFields, r));

  const gridPhaseEnvelope = envelopeData
    ? createPoints(envelopeData['y'], envelopeData['x']).map(r => toRowObject(colFields, r))
    : [];

  return { traces, gridPhaseEnvelope, gridOperationData };
}

export function buildMultipleOperationTraces(
  operation: any,
  resultIndex: number,
  perforationDepth: number,
  allOps: any[],
  temperatureResults: TemperatureResults[],
  pressureResults: PressureResults[],
  temperatureUnit: string,
  pressureUnit: string,
  depthUnit: string
): any[] {
  const perforationIndex = temperatureResults[resultIndex].phases[0].temperatureResultsTable.rows.findIndex(
    x => (perforationDepth - x[0]) < 0.001
  );
  const temperatureRows = temperatureResults[resultIndex].phases[0].temperatureResultsTable.rows.slice(0, perforationIndex + 1);
  const pressureRows = pressureResults[resultIndex].phases[0].pressureResultsTable.rows.slice(0, perforationIndex + 1);

  const temperatures = temperatureRows.map(x => x[2]);
  const pressures = pressureRows.map(x => x[2]);
  const depths = temperatureRows.map(x => x[0]);

  const legendGroupName = 'group' + resultIndex;

  const tempPressureLine = {
    x: temperatures,
    y: pressures,
    name: temperatureResults[resultIndex].name,
    legendgroup: legendGroupName,
    customdata: depths,
    hovertemplate: `Temperature: %{x} ${temperatureUnit}<br>Pressure: %{y} ${pressureUnit}<br>Depth: %{customdata} ${depthUnit}<extra></extra>`
  };

  const markerTraces = buildWellheadAndPerfsTraces(
    resultIndex, perforationDepth, allOps, operation, legendGroupName, temperatureResults, pressureResults
  );

  // Match original push order: wellhead+shoe, then the T-P line
  return [...markerTraces, tempPressureLine];
}
